import {
  Box, Stack, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import { createPatient, updatePatient } from '../api/patientApi';
import { useSnackbar } from './Snackbar';

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  mobileNo: Yup.string()
    .matches(/^[0-9]{10}$/, 'Must be a 10-digit mobile number')
    .required('Mobile number is required'),
  dateOfBirth: Yup.date()
    .required('Date of birth is required')
    .max(new Date(), 'Date of birth must be in the past'),
});

/**
 * One form for both adding a patient and correcting an existing one.
 *
 * Clinicians kept discovering a typo in the name, a wrong digit in the mobile
 * number, or a mis-keyed date of birth only after saving, and had no way back —
 * so the same fields and the same rules now serve the edit case. Date of birth
 * especially: it drives every age band in the report, so a wrong one quietly
 * skews the whole developmental profile until it is fixed.
 *
 * @param open      whether the dialog is shown
 * @param patient   the record being edited; omit (or pass null) to add
 * @param onClose   called on cancel or after a successful save
 * @param onSaved   called with the saved patient `{ id, name, mobileNo, dateOfBirth }`
 */
const PatientFormDialog = ({ open, patient = null, onClose, onSaved }) => {
  const { showSnackbar } = useSnackbar();
  const isEdit = Boolean(patient?.id);

  const formik = useFormik({
    // The dialog stays mounted between openings, so without this the form keeps
    // whichever patient it was first opened with.
    enableReinitialize: true,
    initialValues: {
      name: patient?.name || '',
      mobileNo: patient?.mobileNo || '',
      dateOfBirth: patient?.dateOfBirth ? dayjs(patient.dateOfBirth).format('YYYY-MM-DD') : '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        name: values.name.trim(),
        mobileNo: values.mobileNo,
        dateOfBirth: dayjs(values.dateOfBirth).format('YYYY-MM-DD'),
      };

      const failure = isEdit ? 'Could not save the changes.' : 'Could not add the patient.';

      try {
        const response = isEdit
          ? await updatePatient({ ...payload, id: patient.id })
          : await createPatient(payload);

        if (!response.data?.succeeded) {
          showSnackbar(response.data?.messages?.[0] || failure, 'error');
          return;
        }

        // Add returns the new id; Update echoes the one we sent.
        const saved = { ...payload, id: isEdit ? patient.id : response.data.data, source: 'local' };
        onSaved?.(saved);
        showSnackbar(isEdit ? `${payload.name} updated.` : `${payload.name} added.`, 'success');
        resetForm();
        onClose?.();
      } catch (e) {
        showSnackbar(e?.response?.data?.messages?.[0] || failure, 'error');
      }
    },
  });

  const cancel = () => { formik.resetForm(); onClose?.(); };

  return (
    <Dialog open={open} onClose={cancel} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit patient' : 'Add patient'}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ pt: 1 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth label="Name" name="name" autoFocus
              value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              fullWidth label="Mobile number" name="mobileNo" inputMode="numeric"
              value={formik.values.mobileNo} onChange={formik.handleChange} onBlur={formik.handleBlur}
              error={formik.touched.mobileNo && Boolean(formik.errors.mobileNo)}
              helperText={formik.touched.mobileNo && formik.errors.mobileNo}
            />
            <DatePicker
              label="Date of birth"
              value={formik.values.dateOfBirth ? dayjs(formik.values.dateOfBirth) : null}
              onChange={(value) => formik.setFieldValue('dateOfBirth', value ? value.format('YYYY-MM-DD') : '')}
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  onBlur: () => formik.setFieldTouched('dateOfBirth', true),
                  error: formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth),
                  helperText: (formik.touched.dateOfBirth && formik.errors.dateOfBirth)
                    || 'Used to place the child on the developmental age bands',
                },
              }}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel}>Cancel</Button>
        <Button variant="contained" onClick={formik.handleSubmit} disabled={formik.isSubmitting}>
          {formik.isSubmitting
            ? (isEdit ? 'Saving…' : 'Adding…')
            : (isEdit ? 'Save changes' : 'Add patient')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientFormDialog;
